import React, { useEffect, useMemo, useState } from 'react';
import {
  createMasterCatalogueItem,
  updateMasterCatalogueItem,
  MasterCatalogueItem,
  CreateCatalogueItemPayload,
  PackPriceChoice,
} from '../../lib/masterCatalogueApi';
import { formatCurrency } from '../../utils/priceUtils';
import { PACK_UNITS, MEASURE_UNITS, canonicalUnit, catalogueUnitError } from '../../utils/unitVocabulary';
import {
  DEFAULT_PACK_NAME,
  UnitConversion,
  ConversionRole,
  conversionRole,
  conversionsForItem,
  describeConversion,
  legacyPackFields,
  readableSize,
  restatesOwnUnit,
} from '../../utils/unitConversions';
// The consistency rule, and the same one the server refuses a save with - so
// the message beside a row and the message the API would return are one
// sentence rather than two spellings of it.
import { shownByUnit } from './costPriceTiers';
import { VendorSelect } from '../../components/VendorSelect';
import { checkRowAgainstGraph, buildGraph, reachableUnits } from '../../utils/unitConversionGraph';
// The category vocabulary. Fetched once per mounted form — it is a list of a
// dozen or so values, read by a select, never per row and never per keystroke.
import { ItemCategory, categoryOptions, listItemCategories, selectedCategoryOption } from '../../lib/itemCategoryApi';
import { fieldErrorsOf } from '../../lib/apiErrors';
import { keywordList } from './keywordList';

/**
 * The Master Catalogue item form - the ONE of it.
 *
 * Extracted from `pages/MasterCataloguePage.tsx` on 2026-09-01, where it was a
 * ~410-line `itemFormFields` block plus ~180 lines of state shared by two modals
 * on that page. It moved because a THIRD caller arrived: a quotation line the
 * catalogue has never heard of now offers to add it, and the owner's instruction
 * was that it ask "the same thing that we ask for a master catalog item... Just
 * copy it."
 *
 * Copying it is precisely what this file exists to prevent. A second spelling of
 * `validateItemForm` or `conversionRowIssues` is CLAUDE.md's #1 historical
 * defect shape applied to the form that decides an item's PACK RATIO - and a
 * pack ratio that disagrees with itself is how an RFQ for 100 PCS was once
 * quoted at the price of 100 CASES (TECH_DEBT #138).
 *
 * `conversionRowIssues` below runs the SAME `checkRowAgainstGraph` the server
 * refuses a save with, so the message beside a row and the message the API would
 * return stay one sentence. That rule landed on `qa` while this extraction was
 * in flight; it is carried here verbatim rather than merged by hand, because a
 * form that validated conversions differently from the catalogue page would
 * reintroduce exactly the divergence the graph work exists to close.
 *
 * The component owns its state and its save; callers hand it a seed and a
 * callback and do not reach inside. `mode` picks the endpoint - create and edit
 * are the same form because they are the same question, and the only difference
 * is the verb the payload goes out on.
 */
/** Every unit a conversion row may name, on either side. */
const CONVERSION_UNITS: string[] = Array.from(new Set<string>([...PACK_UNITS, ...MEASURE_UNITS]));

/**
 * One conversion row while it is being typed. The quantities are STRINGS because
 * that is what a number input holds: a half-typed "1." and an emptied box are
 * both states a person passes through, and `Number('')` is 0 — the exact
 * missing-is-not-zero confusion invariant 8 exists to stop. They become real
 * numbers once, on the way into the payload.
 *
 * `key` is a stable identity for React across insertions and removals. Using the
 * array index instead re-keys every row below a removed one, and the focused
 * input jumps to a different row mid-edit.
 */
type DraftConversion = {
  key: string;
  fromQty: string;
  fromUnit: string;
  toQty: string;
  toUnit: string;
  source: UnitConversion['source'];
};

let draftRowSeq = 0;
const nextDraftKey = () => `conv-${(draftRowSeq += 1)}`;

const draftFrom = (c: UnitConversion): DraftConversion => ({
  key: nextDraftKey(),
  fromQty: String(c.fromQty),
  fromUnit: c.fromUnit,
  toQty: String(c.toQty),
  toUnit: c.toUnit,
  source: c.source,
});

/** A draft as the payload states it. A row still being typed comes back null. */
const draftToConversion = (d: DraftConversion): UnitConversion | null => {
  const fromQty = Number(d.fromQty);
  const toQty = Number(d.toQty);
  if (!d.fromQty || !d.toQty || !d.fromUnit || !d.toUnit) return null;
  if (!Number.isFinite(fromQty) || !Number.isFinite(toQty) || fromQty <= 0 || toQty <= 0) return null;
  return { fromQty, fromUnit: d.fromUnit, toQty, toUnit: d.toUnit, source: d.source };
};

/**
 * The option a typed row unit names, or null when it names none: the option in any case, else
 * its canonical spelling ("kgs" -> KG) when that is an option. Blank is '' (D157.4).
 */
function rowUnitOnList(typed: string, options: string[]): string | null {
  const text = typed.trim();
  if (!text) return '';
  const canonical = canonicalUnit(text);
  return options.find((o) => o.toUpperCase() === text.toUpperCase()) ?? options.find((o) => o === canonical) ?? null;
}

type RowIssue = { level: 'error' | 'warning'; message: string; unitField?: 'fromUnit' | 'toUnit' };

/**
 * What is wrong with each row, in order — one issue per row, or null.
 *
 * The line between an error and a warning is whether saving would write
 * something FALSE. An empty half and a zero state nothing, so they block. A row
 * that says a unit converts into itself blocks only where it would be read as
 * the item's SIZE, because that stores a `unit_size` contradicting the unit the
 * item is counted in — "1 KG = 5 KG" makes a kilogram weigh five. The same shape
 * read as a PACK ratio is the ordinary legacy shape (a CASE-counted item whose
 * case holds 6) which every reader downstream already handles correctly, so
 * blocking it would refuse rows that are stored and priced right today.
 *
 * The CONSISTENCY check — does this row contradict what the rows above it
 * already state — is the same `checkRowAgainstGraph` the server refuses the save
 * with, so the message beside the row and the message the API would return are
 * one sentence, not two spellings of one. It is checked incrementally, in list
 * order, so the row flagged is the one that broke the agreement rather than the
 * first row of the pair.
 */
function conversionRowIssues(rows: DraftConversion[], unit: unknown, unitOptions: string[]): (RowIssue | null)[] {
  const rolesSeen = new Set<ConversionRole>();
  // Rows accepted so far, for the incremental consistency check below. See
  // `validateConversionList` for why one path per row tests all of them.
  const accepted: UnitConversion[] = [];
  // The whole list, because which row is the pack ratio depends on whether the
  // item's own Unit is the pack or its contents, and only the list says which
  // (`unitConversions.packedUnit`). Deciding row by row read a vendor row's
  // "1 CASE = 12 PCS" as setting nothing.
  const list = rows.map(draftToConversion).filter(Boolean) as UnitConversion[];
  return rows.map((row) => {
    if (!row.fromUnit.trim() || !row.toUnit.trim()) {
      return { level: 'error', message: 'Pick both units, or remove the row' } as RowIssue;
    }
    const unknownSide = (['fromUnit', 'toUnit'] as const).find((side) => rowUnitOnList(row[side], unitOptions) === null);
    if (unknownSide) {
      return { level: 'error', message: `"${row[unknownSide].trim()}" is not a unit on the list`, unitField: unknownSide } as RowIssue;
    }
    const fromQty = Number(row.fromQty);
    const toQty = Number(row.toQty);
    if (!row.fromQty || !row.toQty || !Number.isFinite(fromQty) || !Number.isFinite(toQty)) {
      return { level: 'error', message: 'Enter both quantities, or remove the row' } as RowIssue;
    }
    if (fromQty <= 0 || toQty <= 0) {
      return { level: 'error', message: 'Both quantities must be greater than zero' } as RowIssue;
    }

    const candidate = draftToConversion(row) as UnitConversion;

    // Does this row contradict what the rows above it already state? An item
    // saying "1 CASE = 10 PCS" and "1 PCS = 20 KG" has already said one case is
    // 200 kg; "1 CASE = 210 KG" beside them is not a third fact. Blocked, and
    // the message names the value the existing rows derive — the same sentence
    // the server would refuse the save with.
    const verdict = checkRowAgainstGraph(accepted, candidate);
    if (!verdict.ok) {
      // Not pushed into `accepted`: a contradicting row must not go on to
      // constrain the rows below it, or one bad row flags three.
      return { level: 'error', message: verdict.message } as RowIssue;
    }
    accepted.push(candidate);

    const role = conversionRole(candidate, unit, list);
    const sameUnit = canonicalUnit(row.fromUnit) === canonicalUnit(row.toUnit);

    if (sameUnit && role === 'size') {
      return {
        level: 'error',
        message: `This item is counted in ${canonicalUnit(row.toUnit)} — a unit cannot be a different size of itself`,
      } as RowIssue;
    }
    if (sameUnit) {
      const alreadySeen = rolesSeen.has(role);
      rolesSeen.add(role);
      return {
        level: 'warning',
        message: alreadySeen
          ? `Both sides say ${canonicalUnit(row.toUnit) || row.toUnit}, and a row above already sets this.`
          : `Both sides say ${canonicalUnit(row.toUnit) || row.toUnit}. Give the outer pack its own name (CARTON, BOX) so the row reads properly — the number is still read as the pack count.`,
      } as RowIssue;
    }
    if (role !== 'other' && rolesSeen.has(role)) {
      // Deliberately no longer says "nothing is priced off it" — that stopped
      // being true when the conversion graph landed. A second same-role row does
      // not set the legacy COLUMN, but it is a real edge: it is checked for
      // consistency against every other row, and a price is derived at every
      // unit it reaches.
      return {
        level: 'warning',
        message: role === 'pack'
          ? 'A row above already sets Units per case, so that column takes the other one. This row still counts: it is checked against the others and priced from.'
          : 'A row above already sets Unit size, so that column takes the other one. This row still counts: it is checked against the others and priced from.',
      } as RowIssue;
    }
    rolesSeen.add(role);
    return null;
  });
}

/**
 * The badge under a row, naming which stored column it feeds — or `null` for a
 * row that feeds neither.
 *
 * `role === 'other'` used to print "no stored column", with a hint explaining
 * that the row is still real and still priced from. Reported (owner,
 * 2026-09-24): "no stored column" reads as a refusal of a row that saves,
 * validates and prices — the same misreading the hint text itself was already
 * fighting (see the retired comment this replaced, and `git log` on this
 * file). Rather than word it a fourth way, the owner asked for no badge at
 * all on such a row: `pack` and `size` still earn one, `other` earns silence.
 *
 * The PT/GAL "this system holds no ratio for it" sentence this badge used to
 * carry for a name-ambiguous unit left with it. It is not shown anywhere else
 * in the UI — checked, there is no other consumer — so removing this badge
 * loses that specific warning on screen; the underlying fact stays documented
 * in code at `packConversion.ts`'s `LITRE_PER_UNIT` header (TECH_DEBT #144).
 * Reported rather than re-homed: whether a person entering a PT conversion
 * needs a screen warning for it is a product question this change does not
 * answer on its own.
 */
export function roleLabel(role: ConversionRole, toUnit: string): { text: string; hint: string } | null {
  if (role === 'pack') {
    return {
      text: 'sets units per case',
      hint: 'This row states how many one pack holds, so it is what Units per case stores — whether the item is counted in the pack (a CASE priced per case) or in what the pack holds (a PCS sold by the case).',
    };
  }
  if (role === 'size') {
    return {
      text: 'sets unit size',
      hint: 'A weight or volume for one of this item, so it can be priced per kg or per litre.',
    };
  }
  return null;
}
export type ItemFormErrors = Partial<
  Record<'item_name' | 'unit' | 'item_price' | 'unitConversions' | 'vendorId', string>
>;

/**
 * D107.4: no price exists without a registered vendor. The sentence is the server's
 * (422 CATALOGUE_SUPPLIER_REQUIRED), so the form and the server refuse in one voice; the
 * upload dialog on the Master Catalogue page says it too.
 */
export const SUPPLIER_REQUIRED_MESSAGE = 'Pick the supplier from the vendor list.';

export function validateItemForm(form: Partial<CreateCatalogueItemPayload>): ItemFormErrors {
  const errors: ItemFormErrors = {};
  if (!form.item_name || !String(form.item_name).trim()) {
    errors.item_name = 'Item name is required';
  }
  if (!form.unit || !String(form.unit).trim()) {
    errors.unit = 'Unit is required';
  } else if (catalogueUnitError(form.unit)) {
    // A unit and nothing else — not "2", "5 KG", "xyz CASE", "xyz" or "--"
    // (USC-BUG-0016/0017/0043; QA 2026-09-24). One owner for the rule and the
    // sentence, `unitVocabulary.catalogueUnitError`, which the server calls too.
    errors.unit = catalogueUnitError(form.unit) as string;
  }
  const price = form.item_price;
  if (price === undefined || price === null || (typeof price === 'number' && Number.isNaN(price))) {
    errors.item_price = 'Item price is required';
  } else if (typeof price === 'number' && price <= 0) {
    errors.item_price = 'Item price must be greater than 0';
  }
  // A typed name is not a vendor: only a vendor-list pick carries an id (D86).
  if (!form.vendorId) errors.vendorId = SUPPLIER_REQUIRED_MESSAGE;

  // The conversion rows validate row by row, in `conversionRowIssues` — a
  // message about one row belongs under that row and not in a form-wide bag
  // keyed by column name, which is what the two-row version had to do.

  return errors;
}

export function parseServerError(err: unknown): { message: string; fieldErrors?: Record<string, string> } {
  if (err instanceof Error) {
    // D114.7: the message is plain text; the refused fields ride on the error.
    const fieldErrors = fieldErrorsOf(err);
    return { message: err.message, fieldErrors: Object.keys(fieldErrors).length ? fieldErrors : undefined };
  }
  return { message: 'Request failed' };
}

/**
 * D119.3: the server's refusal of a Units / Case change on a row priced per the pack. The two
 * outcomes are the SERVER's arithmetic (`utils/packPriceChoice`), carried in the error's
 * `details`; the form words them and never works a price out itself.
 */
export const PACK_PRICE_CHOICE_REQUIRED = 'CATALOGUE_PACK_PRICE_CHOICE_REQUIRED';

// `packPrice` is null when the server marked the option `unavailable` (the restated pack price
// would round to TT$0.00); the form shows it disabled and never works the price out itself.
type PackOutcome = { packPrice: number | null; piecePrice: number; unavailable?: 'under_a_cent' };
export type PackPricePrompt = {
  unit: string;
  pieceUnit: string | null;
  oldCount: number;
  newCount: number;
  keep_pack_price: PackOutcome & { packPrice: number }; // keeping the stored price is always available
  keep_piece_price: PackOutcome;
};

const isOutcome = (v: unknown): v is PackOutcome =>
  !!v && typeof v === 'object' &&
  (typeof (v as PackOutcome).packPrice === 'number' ||
    ((v as PackOutcome).packPrice === null && (v as PackOutcome).unavailable === 'under_a_cent')) &&
  typeof (v as PackOutcome).piecePrice === 'number';

/** The prompt a refusal carries, or null when its `details` are not the shape the server sends. */
export function readPackPricePrompt(err: unknown): PackPricePrompt | null {
  if ((err as { code?: unknown } | null)?.code !== PACK_PRICE_CHOICE_REQUIRED) return null;
  const d = (err as { details?: Record<string, unknown> }).details;
  if (!d || typeof d.unit !== 'string' || typeof d.oldCount !== 'number' || typeof d.newCount !== 'number') return null;
  if (!isOutcome(d.keep_pack_price) || !isOutcome(d.keep_piece_price)) return null;
  const keepPack = d.keep_pack_price;
  if (keepPack.packPrice === null) return null;
  return {
    unit: d.unit,
    pieceUnit: typeof d.pieceUnit === 'string' ? d.pieceUnit : null,
    oldCount: d.oldCount,
    newCount: d.newCount,
    keep_pack_price: { ...keepPack, packPrice: keepPack.packPrice },
    keep_piece_price: d.keep_piece_price,
  };
}

export type CatalogueItemFormMode =
  | { kind: 'create' }
  /** Edit needs the row's id; the form never infers it from the seed. */
  | { kind: 'edit'; item: MasterCatalogueItem };

export interface CatalogueItemFormProps {
  mode: CatalogueItemFormMode;
  /**
   * Prefill for a NEW item, its conversion rows and keywords included (its list, else its
   * units_per_case / unit_size, D157.11; D159.2). Ignored on 'edit' in favour of the row itself.
   */
  seed?: Partial<CreateCatalogueItemPayload>;
  /**
   * Rendered above the fields. The quotation caller uses it to name the line
   * being added and to warn when near-duplicate rows already exist.
   */
  banner?: React.ReactNode;
  submitLabel?: string;
  onCancel: () => void;
  /**
   * The saved row. Callers refresh their own list / re-price their own line.
   * `null` only when `onSubmitOverride` is given and the row it produced is not
   * a `MasterCatalogueItem` this form can see (see `onSubmitOverride`).
   */
  onSaved: (item: MasterCatalogueItem | null) => void;
  /**
   * The catalogue-addition review queue's edit-then-approve path: when given,
   * Save calls THIS instead of `createMasterCatalogueItem` /
   * `updateMasterCatalogueItem`, with the exact payload this form would
   * otherwise have posted (same `validateItemForm`, same conversion-row
   * checks, same trimmed `item_name`/`unit`). The caller owns what "saving"
   * means — there, approving a submission with this payload as its
   * `payloadOverride` — and returns whatever `MasterCatalogueItem` resulted,
   * or `null` when the decision endpoint answered with the submission record
   * rather than a catalogue row.
   *
   * Absent, the form's own create/update call runs exactly as it always has —
   * this is additive only, never a fork of the save path.
   */
  onSubmitOverride?: (payload: CreateCatalogueItemPayload) => Promise<MasterCatalogueItem | null>;
}

/** What a fresh catalogue item starts from - the page's own `openAddItem` seed. */
/**
 * The form state a NEW item starts from.
 *
 * `currency: 'TTD'` because a catalogue price is stated in TT$ (owner's call,
 * 2026-09-08). The server defaults an unstated currency the same way — see
 * `catalogueCurrencyForWrite` — so this is not the rule, it is the rule made
 * VISIBLE: the person adding an item sees TT$ selected and can change it,
 * rather than saving a blank box that silently priced the row in US$ at about
 * 6.8x (TECH_DEBT #70).
 *
 * Deliberately NOT in `seedFromItem`. An EDIT shows whatever the row stored,
 * blank included: seeding TT$ there would let an ordinary save stamp TT$ onto a
 * legacy `quotation_historical` row whose blank currency is genuinely US$, and
 * quote it at a seventh of its price. Which stored blanks are which is #328.
 */
export const BLANK_CATALOGUE_ITEM: Partial<CreateCatalogueItemPayload> = {
  item_name: '',
  source: 'manual',
  currency: 'TTD',
  // USC-BUG-0019: a new item states what its unticked VAT box shows. Absent,
  // an untouched box saved nothing and the row landed on the schema's null
  // ("—", unknown) instead of "No". Null stays for writers that truly do not
  // know (quotation harvest, invoice sync); a person adding an item has said.
  vat_applicable: false,
};

/** The form state an EDIT starts from, read off a stored row. */
export function seedFromItem(item: MasterCatalogueItem): Partial<CreateCatalogueItemPayload> {
  return {
    item_name: item.item_name,
    category: item.category ?? undefined,
    sub_category: item.sub_category ?? undefined,
    source: item.source,
    item_price: item.item_price ?? undefined,
    unit: item.unit ?? undefined,
    vat_applicable: item.vat_applicable ?? undefined,
    keywords: item.keywords,
    supplier: item.supplier ?? undefined,
    vendorId: item.vendorId ?? undefined,
    currency: item.currency ?? undefined,
    units_per_case: item.units_per_case ?? undefined,
    unit_size: item.unit_size ?? undefined,
    // The schema default is false, so a row stored without the field is not fresh (D170.6).
    is_fresh: item.is_fresh === true,
  };
}

export default function CatalogueItemForm({
  mode,
  seed,
  banner,
  submitLabel,
  onCancel,
  onSaved,
  onSubmitOverride,
}: CatalogueItemFormProps) {
  const isEdit = mode.kind === 'edit';
  // Seeded ONCE, through lazy initial state, never in an effect. An effect would
  // re-seed on every parent re-render and wipe what somebody had typed; a caller
  // that wants a fresh seed remounts the form with a new `key`.
  const [itemForm, setItemForm] = useState<Partial<CreateCatalogueItemPayload>>(() =>
    mode.kind === 'edit' ? seedFromItem(mode.item) : { ...BLANK_CATALOGUE_ITEM, ...(seed || {}) }
  );
  // The Keywords box as typed, read once off the seeded keywords; saved as `keywordList` of it (D159.2).
  const [keywordText, setKeywordText] = useState(() => (itemForm.keywords || []).join(', '));
  /**
   * The rows that only restate the item's own Unit — "1 PCS = 1 LBS" on a row
   * already counted in LBS. Split off the seed, never rendered, and put back
   * verbatim on save.
   *
   * ── Why they are carried rather than dropped ─────────────────────────────
   * Hiding a row is a screen decision. Deleting one is a data decision, and
   * making the second one happen as a side effect of the first would mean that
   * opening an item to fix its Category silently rewrote its `unitConversions`
   * and the two columns the server derives from them. Nobody asked for that on
   * that save, and it is the shape this form's own comments call out twice
   * (a removed row reappearing, a dropped field found in the browser rather
   * than in review).
   *
   * So the payload is byte-for-byte what it would have been before this
   * existed. The rows leave the database when the vendor sync replaces one with
   * a real ratio, or when somebody decides to clean them up — not here.
   *
   * Seeded ONCE with the visible rows below, off the same lazy initial state,
   * so the two halves cannot disagree about which list they came from.
   */
  // Edit loads the row's rows; create loads its seed's, so edit-and-approve keeps a submission's pack (D157.11).
  const loadedFrom = mode.kind === 'edit' ? mode.item : seed;
  const [hiddenConversions] = useState<UnitConversion[]>(() =>
    conversionsForItem(loadedFrom).filter((c) => restatesOwnUnit(c, loadedFrom?.unit))
  );
  const [conversions, setConversions] = useState<DraftConversion[]>(() =>
    conversionsForItem(loadedFrom).filter((c) => !restatesOwnUnit(c, loadedFrom?.unit)).map(draftFrom)
  );
  // The units the rows were loaded with, read once: a word typed since is not on the list.
  const [storedRowUnits] = useState(() => conversions.flatMap((r) => [r.fromUnit, r.toUnit]));
  // The row unit box being typed in, `${key}:fromUnit`; its unknown-unit error waits until it is left.
  const [focusedUnit, setFocusedUnit] = useState<string | null>(null);
  const [itemFormTouched, setItemFormTouched] = useState<Record<string, boolean>>({});
  const [itemFormSubmitAttempted, setItemFormSubmitAttempted] = useState(false);
  const [itemFormServerErrors, setItemFormServerErrors] = useState<Record<string, string>>({});
  const [savingItem, setSavingItem] = useState(false);
  /** A refusal the server gave for no one field (a duplicate, D24), shown above the buttons. */
  const [saveRefusal, setSaveRefusal] = useState<string | null>(null);
  // D86: a saved supplier that is not a vendor-list entry. Saving waits for a real pick.
  const [supplierUnmatched, setSupplierUnmatched] = useState(false);
  // D119.3: the server asked which price to keep, and the answer. Save waits for the answer.
  const [packPrompt, setPackPrompt] = useState<PackPricePrompt | null>(null);
  const [packChoice, setPackChoice] = useState<PackPriceChoice | null>(null);

  /**
   * The category vocabulary — the reason Category is a select and not a text
   * box any more.
   *
   * The live filter held 19 values for six real ones: three casings of
   * "Electronics", two of "Provisions", the literal string `Null`, and five
   * pieces of typing-test junk sitting beside the client's actual ship
   * departments. A category that means six different strings cannot group a
   * price list or split an order by who pays for it, which is the entire reason
   * she asked for the field.
   *
   * A failed fetch leaves the list EMPTY, and an empty list still shows the
   * row's own stored category (see `categoryChoices`) — so a network problem
   * degrades to "you can keep what is there", never to "your category silently
   * blanked".
   */
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  useEffect(() => {
    let alive = true;
    listItemCategories()
      .then((res) => {
        if (alive) setCategories(res.data?.categories || []);
      })
      .catch((e) => {
        console.error('Could not load item categories:', e);
      });
    return () => {
      alive = false;
    };
  }, []);

  // The question was about THESE rows, this Unit and this price. Any of them moving makes the
  // two prices it showed stale, so it is withdrawn and the server asks again on the next Save.
  useEffect(() => {
    setPackPrompt(null);
    setPackChoice(null);
  }, [conversions, itemForm.unit, itemForm.item_price]);

  /**
   * The vocabulary, plus this row's own category when that is not in it.
   *
   * The rows written before the list existed carry values the vocabulary does
   * not know, and they STAY VISIBLE and selected: a row is not corrected by
   * hiding what it says, and a save that only changes a price must not silently
   * re-file the item. They are marked "(not in list)" so the ones still needing
   * attention are the ones that look like they do.
   */
  const categoryChoices = useMemo(
    () => categoryOptions(categories, [itemForm.category]),
    [categories, itemForm.category]
  );

  /**
   * The units a row may name: the vocabulary, the item's own Unit (a box people type "each" into)
   * and the units the rows were loaded with (a supplier's own pack word survives `normalizeConversion`).
   */
  const conversionUnitOptions = useMemo(() => {
    const seen = new Set(CONVERSION_UNITS);
    const extra: string[] = [];
    for (const u of [String(itemForm.unit ?? '').trim(), ...storedRowUnits]) {
      if (u && !seen.has(u)) {
        seen.add(u);
        extra.push(u);
      }
    }
    return extra.length ? [...extra, ...CONVERSION_UNITS] : CONVERSION_UNITS;
  }, [itemForm.unit, storedRowUnits]);

  const itemFormErrors = useMemo(() => validateItemForm(itemForm), [itemForm]);
  const rowIssues = useMemo(
    () => conversionRowIssues(conversions, itemForm.unit, conversionUnitOptions),
    [conversions, itemForm.unit, conversionUnitOptions]
  );
  const isItemFormValid =
    Object.keys(itemFormErrors).length === 0 && !rowIssues.some((i) => i?.level === 'error');

  // ── Unit conversion row handlers ──────────────────────────────────────────

  /** A blank row, seeded with the shape most rows have: "1 X = N Y". */
  const addConversionRow = () =>
    setConversions((rows) => [
      ...rows,
      {
        key: nextDraftKey(),
        fromQty: '1',
        // The first row an item gets is almost always its case, and its right
        // side is almost always what the item is counted in. A wrong guess is
        // retyped in its box.
        fromUnit: rows.length === 0 ? DEFAULT_PACK_NAME : '',
        toQty: '',
        toUnit: rows.length === 0 ? (canonicalUnit(itemForm.unit) ?? '') : '',
        source: 'manual',
      },
    ]);

  const removeConversionRow = (key: string) =>
    setConversions((rows) => rows.filter((r) => r.key !== key));

  const updateConversionRow = (key: string, patch: Partial<DraftConversion>) =>
    setConversions((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  /** Leaving a unit box stores the unit the text names ("kgs" -> KG); unknown text stays, refused. */
  const leaveUnitBox = (key: string, side: 'fromUnit' | 'toUnit', typed: string) => {
    setFocusedUnit(null);
    const named = rowUnitOnList(typed, conversionUnitOptions);
    if (named !== null && named !== typed) {
      updateConversionRow(key, side === 'fromUnit' ? { fromUnit: named } : { toUnit: named });
    }
  };

  /**
   * What these rows will actually be STORED as — `units_per_case` and
   * `unit_size`, read out by the same owner the server derives them with.
   *
   * On screen because the two are not obvious from the rows: which row is the
   * pack ratio depends on the item's own Unit, a second row of the same kind is
   * recorded but not read, and a row naming neither the item's unit nor a
   * convertible size sets nothing at all.
   */
  /**
   * The rows as conversions, once. Every reading below is decided against the
   * whole LIST rather than row by row — `unitConversions.packedUnit` needs it to
   * tell an item counted in the pack from one counted in what the pack holds.
   */
  const conversionList = useMemo(
    () =>
      [
        ...(conversions.map(draftToConversion).filter(Boolean) as UnitConversion[]),
        // The hidden rows are part of the list every reading below is decided
        // against, because they are part of the list the SERVER will hold. A
        // badge or a derived column computed against a shorter list than the one
        // being saved is a screen disagreeing with its own payload.
        ...hiddenConversions,
      ],
    [conversions, hiddenConversions]
  );

  const derivedPackFields = useMemo(
    () => legacyPackFields(conversionList, itemForm.unit),
    [conversionList, itemForm.unit]
  );

  /**
   * Every unit this item's rows can be priced at, in the order the graph reaches
   * them — the item's own unit first, then outward by hop.
   *
   * The REACH only, never a price: the money is computed once, on the server, by
   * `costEngine.computeGraphPriceLevels`, and stored in `priceLevels.byUnit`.
   * Re-deriving it here would be a second price engine in a second language, and
   * two engines answering "what does this cost per kg" is the exact drift the
   * mirrored-util harness exists to prevent. What this line answers is the
   * question the person editing actually has — "is my new row connected to
   * anything?" — which is pure graph, and already mirrored.
   */
  /**
   * The per-unit prices this item ALREADY has stored, in the order the server
   * derived them (its own unit first, then outward by hop).
   *
   * Read, never recomputed — see the block that renders it. Empty for a new
   * item, and for any row written before `byUnit` existed: it is populated on
   * the next save through the item controller or the next vendor sync, so an
   * untouched legacy row shows the draft "Priced at" line instead.
   */
  // `editItemOpen` on the page; the row under edit is `mode.item` here. Typed
  // rather than optional-chained off nothing, so `byUnit`'s element type still
  // reaches the two `.map`/`.filter` callbacks below — untyped, they were
  // implicit `any` and the stored figures would render whatever came back.
  const editedItem: MasterCatalogueItem | null = mode.kind === 'edit' ? mode.item : null;
  const storedByUnit = useMemo(
    () => (editedItem ? shownByUnit(editedItem) : []).filter((e) => e && e.unit && e.ttAmount > 0),
    [editedItem]
  );

  const reachableFromUnit = useMemo(() => {
    const anchor = canonicalUnit(itemForm.unit) || String(itemForm.unit ?? '').trim().toUpperCase();
    if (!anchor) return [] as string[];
    // `forEach`, not a spread of `.keys()`: the client compiles to ES5, where
    // spreading a Map iterator needs --downlevelIteration.
    const units: string[] = [];
    reachableUnits(buildGraph(conversionList), anchor).forEach((_entry, unit) => units.push(unit));
    // One unit is just the anchor itself, which states nothing worth printing.
    return units.length > 1 ? units : [];
  }, [conversionList, itemForm.unit]);

  /**
   * A stored size the form cannot show as a row — a pack config ("12x28oz"), a
   * size grade, a bare unit. Reported rather than rewritten: it is deriving no
   * per-kg tier and nobody could see that, and the server leaves it alone unless
   * a size row replaces it, so nothing here can destroy it by accident.
   */
  const storedSizeUnreadable =
    Boolean(itemForm.unit_size) &&
    !readableSize(itemForm.unit_size, itemForm.unit) &&
    derivedPackFields.unit_size === null;

  /**
   * The rows as the payload states them.
   *
   * An empty list is sent, never omitted: a person who removed every row means
   * "this item has no conversions", and leaving the field out of the body reads
   * to the update controller as "don't touch them" — the shape that had a
   * removed row coming back on the next open, found in the browser rather than
   * in review.
   */
  const conversionsForPayload = (): UnitConversion[] => [
    ...(conversions.map(draftToConversion).filter(Boolean) as UnitConversion[]),
    // Put back exactly as they were read. See `hiddenConversions`: this save is
    // about whatever the person came here to change, and must not also be the
    // thing that deletes rows they were never shown.
    ...hiddenConversions,
  ];

  /** The whole set restated in one line, so a wrong row is visible before saving. */
  const conversionChain = conversions
    .map(draftToConversion)
    .filter(Boolean)
    .map((c) => describeConversion(c))
    .join('  ·  ');


  const markFieldTouched = (field: string) =>
    setItemFormTouched((t) => ({ ...t, [field]: true }));

  /** The field's own box is not empty — something was typed into it, or loaded into it. */
  const fieldHasContent = (field: keyof ItemFormErrors) => {
    const raw = (itemForm as Record<string, unknown>)[field];
    return raw !== undefined && raw !== null && String(raw).trim() !== '';
  };

  /**
   * USC-BUG-0043 (QA): "not filled in yet" and "filled in wrong" are different.
   *
   * b7401091 (USC-BUG-0009/0010) deferred every field error until the field was
   * blurred or Save was clicked, so someone who opened a row to flip VAT was not
   * shouted at about a Unit they had not reached. That is right for an EMPTY
   * required field. It is wrong for a box that already holds an actively-rejected
   * value: type 123 into Unit and the only text under it stayed the neutral hint
   * "Required. Unit of measure (each, box, kg, ltr…)", which reads as acceptance
   * — the repro that reopened this ticket read exactly that hint off the screen.
   * The save was always refused (`validateItemForm` here, `validateItemFields`
   * server-side); the screen just never said so until Save was pressed.
   *
   * So: a non-empty value that fails validation says so at once; an untouched
   * blank one still waits. The rule itself is owned by `catalogueUnitError`
   * (QA 2026-09-24: a known unit only, no longer `isSizeGradeUom`'s junk
   * shapes) — this is display timing only.
   */
  const shouldShowError = (field: keyof ItemFormErrors) =>
    Boolean(itemFormServerErrors[field]) ||
    (Boolean(itemFormErrors[field]) &&
      (itemFormTouched[field] || itemFormSubmitAttempted || fieldHasContent(field)));

  const errorTextFor = (field: keyof ItemFormErrors): string | null => {
    if (itemFormServerErrors[field]) return itemFormServerErrors[field];
    if (shouldShowError(field)) return (itemFormErrors[field] as string) || null;
    return null;
  };

  const fieldClass = (field: keyof ItemFormErrors) =>
    `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 ${
      shouldShowError(field)
        ? 'border-red-500 focus:ring-red-400 focus:border-red-500'
        : 'border-slate-300 focus:ring-slate-300 focus:border-slate-400'
    }`;

  /**
   * One save for both modes.
   *
   * `unitConversions` is always the LIST, never the two derived columns: the
   * server derives `units_per_case` / `unit_size` from it and ignores whatever
   * the payload still carries in them, so a save cannot store one breakdown in
   * the list and a different one beside it. An EMPTY list is sent rather than
   * omitted - "this item has no conversions" and "do not touch them" are
   * different instructions to the update controller, and conflating them had a
   * removed row reappearing on the next open.
   */
  const handleSave = async () => {
    setItemFormSubmitAttempted(true);
    setItemFormServerErrors({});
    setSaveRefusal(null);
    if (!isItemFormValid) return;
    if (supplierUnmatched) {
      // A vendor id the list no longer holds (a deleted vendor); a missing id never gets here.
      setSaveRefusal('Pick the supplier from the vendor list before saving.');
      return;
    }
    setSavingItem(true);
    try {
      const payload: CreateCatalogueItemPayload = {
        ...(itemForm as CreateCatalogueItemPayload),
        item_name: String(itemForm.item_name || '').trim(),
        unit: String(itemForm.unit || '').trim(),
        // NEW-7: an edit never invents a source. A row stored with none keeps none
        // (shown as Unknown) unless a person picks one - `undefined` drops the key
        // and the server writes only what a body states. D24 reads `manual` as a
        // typed row, so defaulting here turned legacy rows into typed ones.
        source: itemForm.source || (isEdit ? undefined : 'manual'),
        unitConversions: conversionsForPayload(),
        keywords: keywordList(keywordText),
        // D119.3: only once the person has answered the server's question.
        ...(packChoice ? { packPriceChoice: packChoice } : {}),
      };
      const item = onSubmitOverride
        ? await onSubmitOverride(payload)
        : (
            mode.kind === 'edit'
              ? await updateMasterCatalogueItem(mode.item._id, payload)
              : await createMasterCatalogueItem(payload)
          ).data;
      onSaved(item);
    } catch (e) {
      console.error(e);
      const { message, fieldErrors } = parseServerError(e);
      const prompt = readPackPricePrompt(e);
      // D119.3: not a failure - the server needs an answer. Nothing was saved.
      if (prompt) {
        setPackPrompt(prompt);
        setPackChoice(null);
      } else if ((e as { code?: unknown } | null)?.code === PACK_PRICE_CHOICE_REQUIRED) {
        // The right code with details we cannot read: say so, never save around it.
        setSaveRefusal('Changing Units / Case needs a choice of which price to keep, and this form could not show it. Reload and try again.');
      } else if ((e as { code?: unknown } | null)?.code === 'CATALOGUE_SUPPLIER_REQUIRED') {
        // D107.4: the server's code, never its prose, says the Supplier is the problem.
        setItemFormServerErrors({ vendorId: SUPPLIER_REQUIRED_MESSAGE });
      } else if ((e as { code?: unknown } | null)?.code === 'CATALOGUE_PROPOSAL_CURRENCY_MISMATCH') {
        // D170.6: the waiting invoice price is accepted only in its own currency. Nothing was saved.
        const cur = mode.kind === 'edit' ? mode.item.pendingReprice?.currency : null;
        setSaveRefusal(`That is the waiting invoice price${cur ? `, which is in ${cur}` : ''}. Set Currency to ${cur || "the invoice's currency"} to accept it, or type a different price.`);
      } else if (fieldErrors) setItemFormServerErrors(fieldErrors);
      // Shown in the form, never in a native alert(), which sits outside the app and freezes
      // the Chrome extension. A 409 CATALOGUE_ITEM_DUPLICATE (D24) names the row that already
      // exists, on add and on edit, and it has to stay on screen while they decide (CS-2).
      else setSaveRefusal(message || (isEdit ? 'Failed to update item.' : 'Failed to add item.'));
    } finally {
      setSavingItem(false);
    }
  };

  return (
    <>
      {banner}
      <div className="grid gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Item name <span className="text-red-500">*</span>
          </label>
          <input
            value={itemForm.item_name ?? ''}
            onChange={e => {
              setItemForm(f => ({ ...f, item_name: e.target.value }));
              if (itemFormServerErrors.item_name) {
                setItemFormServerErrors(({ item_name: _omit, ...rest }) => rest);
              }
            }}
            onBlur={() => markFieldTouched('item_name')}
            aria-invalid={shouldShowError('item_name')}
            title="Required. The display name for this catalogue item."
            className={fieldClass('item_name')}
            placeholder="Item name"
          />
          {errorTextFor('item_name') ? (
            <p className="mt-1 text-xs text-red-600">{errorTextFor('item_name')}</p>
          ) : (
            <p className="mt-1 text-xs text-slate-500">Required. Used to identify the item across the system.</p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
            <select
              value={selectedCategoryOption(categoryChoices, itemForm.category)}
              onChange={e => setItemForm(f => ({ ...f, category: e.target.value || undefined }))}
              title="The department this item belongs to. Managed under Settings → Item Categories."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-slate-300 focus:border-slate-400"
            >
              <option value="">— None —</option>
              {categoryChoices.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.unlisted ? `${opt.label} (not in list)` : opt.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              Groups a price list, and splits an order by the department that pays for it.
              Add to the list under Settings → Item Categories.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sub category</label>
            <input
              value={itemForm.sub_category ?? ''}
              onChange={e => setItemForm(f => ({ ...f, sub_category: e.target.value || undefined }))}
              title="Optional. Refines the category for clearer reporting."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-300 focus:border-slate-400"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            {/*
              The unit is named in the label, not left to be inferred. A price and
              a pack conversion together are ambiguous on their own — "100" against
              Unit PCS and "1 CASE = 10 PCS" could mean the piece or the case — and
              the server now reads it as the price of ONE of the row's own Unit and
              derives the case from there. Saying so where the number is typed is
              what keeps the typist and the server meaning the same thing.
            */}
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Item price
              {itemForm.unit?.trim() ? (
                <span className="font-normal text-slate-500"> — per {itemForm.unit.trim()}</span>
              ) : null}{' '}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={itemForm.item_price ?? ''}
              onChange={e => {
                const raw = e.target.value;
                setItemForm(f => ({ ...f, item_price: raw === '' ? undefined : Number(raw) }));
                if (itemFormServerErrors.item_price) {
                  setItemFormServerErrors(({ item_price: _omit, ...rest }) => rest);
                }
              }}
              onBlur={() => markFieldTouched('item_price')}
              aria-invalid={shouldShowError('item_price')}
              title="Required. Numeric price; cannot be empty or negative."
              className={fieldClass('item_price')}
            />
            {errorTextFor('item_price') ? (
              <p className="mt-1 text-xs text-red-600">{errorTextFor('item_price')}</p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                {itemForm.unit?.trim()
                  ? `Required. The price of one ${itemForm.unit.trim()}. Other units are worked out from the conversions below.`
                  : 'Required. Must be a non-negative number.'}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Unit <span className="text-red-500">*</span>
            </label>
            <input
              value={itemForm.unit ?? ''}
              onChange={e => {
                setItemForm(f => ({ ...f, unit: e.target.value || undefined }));
                if (itemFormServerErrors.unit) {
                  setItemFormServerErrors(({ unit: _omit, ...rest }) => rest);
                }
              }}
              onBlur={() => markFieldTouched('unit')}
              aria-invalid={shouldShowError('unit')}
              title="Required. A unit only, e.g. each, box, case, kg, ltr."
              placeholder="e.g. each, box, kg"
              list="catalogue-unit-options"
              className={fieldClass('unit')}
            />
            {/* The units the box accepts, offered as the person types. A
                suggestion list, not the rule — `catalogueUnitError` also takes
                the vocabulary's aliases ("each", "ctn") and plurals. */}
            <datalist id="catalogue-unit-options">
              {CONVERSION_UNITS.map(u => (
                <option key={u} value={u} />
              ))}
            </datalist>
            {errorTextFor('unit') ? (
              <p className="mt-1 text-xs text-red-600">{errorTextFor('unit')}</p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">Required. A unit only, no numbers (each, box, case, kg, ltr…).</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="vat"
            checked={itemForm.vat_applicable ?? false}
            onChange={e => setItemForm(f => ({ ...f, vat_applicable: e.target.checked }))}
          />
          <label htmlFor="vat">VAT applicable</label>
        </div>
        {/* D170.6: a ticked row's price moves only by hand; a bill's price waits on the row
            to be accepted. Edit only: a new row takes its first bill's price. */}
        {isEdit && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is-fresh"
              checked={itemForm.is_fresh === true}
              onChange={e => setItemForm(f => ({ ...f, is_fresh: e.target.checked }))}
            />
            <label htmlFor="is-fresh" title="A supplier invoice never changes this item's price; its price waits on the item until you accept it.">
              Fresh (priced monthly by hand)
            </label>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            {/* The label is ours, not VendorSelect's, so the required mark can be a span.
                No "clear" on the box (allowClear): the supplier is changed by picking another. */}
            <label htmlFor="item-supplier" className="block text-sm font-medium text-slate-700 mb-1">
              Supplier / Vendor <span className="text-red-500">*</span>
            </label>
            <VendorSelect
              id="item-supplier"
              ariaLabel="Supplier / Vendor"
              value={{ vendorId: itemForm.vendorId ?? null, name: itemForm.supplier ?? '' }}
              onChange={v => {
                setItemForm(f => ({ ...f, supplier: v.name || null, vendorId: v.vendorId }));
                if (itemFormServerErrors.vendorId) {
                  setItemFormServerErrors(({ vendorId: _omit, ...rest }) => rest);
                }
              }}
              onUnmatchedChange={setSupplierUnmatched}
              allowClear={false}
              placeholder="Pick the vendor this price came from"
            />
            {errorTextFor('vendorId') ? (
              <p role="alert" className="mt-1 text-xs text-red-600">{errorTextFor('vendorId')}</p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">Required. No price is saved without a vendor.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Currency</label>
            <select
              value={itemForm.currency ?? ''}
              onChange={e => setItemForm(f => ({ ...f, currency: e.target.value || undefined }))}
              title="Currency the item price is stored in. A new item starts at TT$ — this supplier's vendors invoice in TT$ — and an item that never stated one keeps its dash until you pick."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-300 focus:border-slate-400 bg-white"
            >
              <option value="">—</option>
              <option value="TTD">TTD (TT$)</option>
              <option value="USD">USD (US$)</option>
            </select>
          </div>
        </div>
        {/*
          The "Quantity" box that sat beside Source was removed 2026-09-01 with
          the schema field. Its own tooltip said "Pack/case quantity associated
          with this price", which is what the conversion rows below state
          properly — and what the field actually held on 4,281 of 5,422 rows was
          the order quantity off the document that created the row. A box that
          invites a person to type a number nothing may read is how the field
          would come back.
        */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
            <select
              value={itemForm.source || (isEdit ? 'unknown' : 'manual')}
              onChange={e => setItemForm(f => ({ ...f, source: e.target.value as CreateCatalogueItemPayload['source'] }))}
              title="Where this catalogue entry originated."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-300 focus:border-slate-400 bg-white"
            >
              <option value="manual">Manual</option>
              <option value="vendor_invoice">Vendor invoice</option>
              <option value="price_list">Price list</option>
              <option value="quotation_historical">Historical quotation</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>
          <div>
            <label htmlFor="item-keywords" className="block text-sm font-medium text-slate-700 mb-1">Keywords</label>
            <input
              id="item-keywords"
              value={keywordText}
              onChange={e => setKeywordText(e.target.value)}
              placeholder="Keywords, comma-separated"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-300 focus:border-slate-400"
            />
            <p className="mt-1 text-xs text-slate-500">
              Comma-separated, e.g. instant noodles, noodles. A keyword groups one kind of product across brands.
            </p>
          </div>
        </div>
        {/*
          Unit conversions — was two fixed boxes, "Units per case" and "Unit size",
          then two fixed ROWS while those two columns were still the whole record.
          `unitConversions` is now the record and the columns are read off it, so
          this is a list: as many rows as the item needs, in any order, each one a
          sentence with both units named. docs/TECH_DEBT.md #155.

          Which stored column a row feeds is decided by `conversionRole` against
          the item's own Unit — the same function the server derives with, mirrored
          — never by the row's position. The badge under each row says which, so a
          row that sets nothing says so on screen instead of being discovered as a
          per-kg price of zero three screens later.
        */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-sm font-medium text-slate-700 mb-1">Unit conversions</p>
          <p className="text-xs text-slate-500 mb-3">
            How this item's units relate. Drives the per-pack / per-kg / per-lb cost breakdown, and
            lets a quotation line be restated in the unit a vessel asked for.
          </p>
          {/* Suggestions for the row unit boxes, filtered as the person types (D157.4). */}
          <datalist id="catalogue-conversion-unit-options">
            {conversionUnitOptions.map(u => (
              <option key={u} value={u} />
            ))}
          </datalist>

          {conversions.length === 0 ? (
            <p className="px-3 py-3 text-center text-xs text-slate-500 bg-white border border-dashed border-slate-300 rounded-lg">
              No conversions yet. Add one to price this item per case or per kg.
            </p>
          ) : (
            <div className="grid gap-2">
              {conversions.map((row, index) => {
                // An unknown typed unit is held back while its box is being typed in, never mid-word (D157.4).
                const rowIssue = rowIssues[index];
                const issue = rowIssue?.unitField && focusedUnit === `${row.key}:${rowIssue.unitField}` ? null : rowIssue;
                const role = conversionRole(draftToConversion(row), itemForm.unit, conversionList);
                const badge = roleLabel(role, row.toUnit);
                const invalid = issue?.level === 'error';
                const boxClass = `px-2 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 ${
                  invalid
                    ? 'border-red-500 focus:ring-red-400 focus:border-red-500'
                    : 'border-slate-300 focus:ring-slate-300 focus:border-slate-400'
                }`;
                return (
                  <div key={row.key}>
                    <div className="grid grid-cols-[54px_minmax(0,1fr)_10px_58px_minmax(0,1fr)_22px] gap-1.5 items-center">
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={row.fromQty}
                        onChange={e => updateConversionRow(row.key, { fromQty: e.target.value })}
                        aria-invalid={invalid}
                        aria-label={`Conversion ${index + 1}: how many of the first unit`}
                        placeholder="1"
                        className={`${boxClass} text-right tabular-nums`}
                      />
                      <input
                        value={row.fromUnit}
                        onChange={e => updateConversionRow(row.key, { fromUnit: e.target.value })}
                        onFocus={() => setFocusedUnit(`${row.key}:fromUnit`)}
                        onBlur={e => leaveUnitBox(row.key, 'fromUnit', e.target.value)}
                        list="catalogue-conversion-unit-options"
                        placeholder="unit"
                        aria-invalid={invalid}
                        aria-label={`Conversion ${index + 1}: the first unit`}
                        className={`${boxClass} bg-white px-1.5`}
                      />
                      <span className="text-sm text-slate-400 text-center" aria-hidden="true">=</span>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={row.toQty}
                        onChange={e => updateConversionRow(row.key, { toQty: e.target.value })}
                        aria-invalid={invalid}
                        aria-label={`Conversion ${index + 1}: how many of the second unit`}
                        placeholder="12"
                        className={`${boxClass} text-right tabular-nums`}
                      />
                      <input
                        value={row.toUnit}
                        onChange={e => updateConversionRow(row.key, { toUnit: e.target.value })}
                        onFocus={() => setFocusedUnit(`${row.key}:toUnit`)}
                        onBlur={e => leaveUnitBox(row.key, 'toUnit', e.target.value)}
                        list="catalogue-conversion-unit-options"
                        placeholder="unit"
                        aria-invalid={invalid}
                        aria-label={`Conversion ${index + 1}: the second unit`}
                        className={`${boxClass} bg-white px-1.5`}
                      />
                      <button
                        type="button"
                        onClick={() => removeConversionRow(row.key)}
                        aria-label={`Remove conversion ${index + 1}`}
                        title="Remove this conversion"
                        className="w-[22px] h-[22px] flex items-center justify-center text-slate-400 rounded hover:bg-red-50 hover:text-red-600"
                      >
                        ×
                      </button>
                    </div>
                    {/*
                      Ungated, unlike the rest of this form. Those fields start
                      empty on a NEW item and go red before anyone has typed, so
                      they wait for a blur. A conversion row is only on screen
                      because somebody added it, and Save refuses while a row
                      is in error — so waiting for a submit that can never happen
                      is how an empty row greyed out Save with nothing saying why.
                      The one wait: an unknown typed unit, until its box is left.
                    */}
                    <div className="mt-1 flex items-start gap-2">
                      {/* `badge` is null for role 'other' — no badge, no
                          tooltip, on the owner's instruction (2026-09-24):
                          "no stored column" read as a refusal of a row that
                          saves, validates and prices. */}
                      {!invalid && badge && (
                        <span
                          title={badge.hint}
                          className="shrink-0 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide rounded bg-emerald-100 text-emerald-800"
                        >
                          {badge.text}
                        </span>
                      )}
                      {issue && (
                        <p className={`text-xs ${invalid ? 'text-red-600' : 'text-amber-700'}`}>
                          {issue.message}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <button
            type="button"
            onClick={addConversionRow}
            title="Add a conversion row"
            className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-dashed border-blue-300 bg-blue-50 text-blue-800 hover:border-solid"
          >
            <span className="text-sm leading-none">+</span>
            Add conversion
          </button>

          {/*
            The server validates the list as well, and refuses a row rather than
            dropping it. Shown here because it is about the block as a whole and
            not about one row — the row that caused it is already marked.
          */}
          {itemFormServerErrors.unitConversions && (
            <p className="mt-2 text-xs text-red-600">{itemFormServerErrors.unitConversions}</p>
          )}

          {storedSizeUnreadable && (
            <p className="mt-2 text-xs text-amber-700">
              The size already stored is “{itemForm.unit_size}”, which is not a plain size, so it is
              deriving no per-kg price. It is left exactly as it is unless a row above replaces it.
            </p>
          )}

          {conversionChain && (
            <p className="mt-2 pt-2 border-t border-dashed border-slate-300 text-xs text-slate-500">
              Reads as <span className="font-medium text-slate-600">{conversionChain}</span>
            </p>
          )}

          {/*
            What the rows come out as in the two columns the rest of the system
            reads. Not decoration: which row is the pack ratio depends on the Unit
            above, a second row of the same kind is recorded but not read, and a
            row naming neither the item's unit nor a convertible size sets nothing
            — none of which is visible from the rows themselves.
          */}
          {(derivedPackFields.units_per_case !== null || derivedPackFields.unit_size !== null) && (
            <p className="mt-1 text-xs text-slate-500">
              Stored as{' '}
              {derivedPackFields.units_per_case !== null && (
                <span className="font-medium text-slate-600">
                  {derivedPackFields.units_per_case} per case
                </span>
              )}
              {derivedPackFields.units_per_case !== null && derivedPackFields.unit_size !== null && ', '}
              {derivedPackFields.unit_size !== null && (
                <span className="font-medium text-slate-600">{derivedPackFields.unit_size} each</span>
              )}
            </p>
          )}

          {/*
            What the rows above are actually WORTH, which is the point of stating
            them. The two columns can hold a two-level pack and nothing more; the
            rows can connect as many units as they like, and this item gets a
            stored price at every one of them.

            The FIGURES are read straight out of `priceLevels.byUnit` — computed
            once, on the server, by `costEngine.computeGraphPriceLevels`. Rendering
            stored numbers is not a second price engine; RE-DERIVING them here
            would be, which is why the draft state below prints units only.

            This is also the reader that keeps `byUnit` from being a field written
            by one path and read by nothing — the shape docs/TECH_DEBT.md #140 is
            about, and the one this whole feature was careful not to repeat.
          */}
          {storedByUnit.length > 0 ? (
            <div className="mt-2 pt-2 border-t border-dashed border-slate-300">
              <p className="text-xs text-slate-500">Priced at, as last saved:</p>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                {storedByUnit.map((e) => (
                  <span key={e.unit} className="text-xs text-slate-600">
                    <span className="font-medium">{e.unit}</span>{' '}
                    TT${e.ttAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                ))}
              </div>
              {/* The rows on screen may have moved since the save that produced
                  those figures; say so rather than letting a stale number read as
                  a live one. */}
              {reachableFromUnit.length > 0 &&
                reachableFromUnit.join(',') !== storedByUnit.map((e) => e.unit).join(',') && (
                  <p className="mt-1 text-xs text-amber-700">
                    The rows above now reach {reachableFromUnit.join(', ')} — save to reprice.
                  </p>
                )}
            </div>
          ) : (
            reachableFromUnit.length > 0 && (
              <p className="mt-1 text-xs text-slate-500">
                Priced at{' '}
                <span className="font-medium text-slate-600">{reachableFromUnit.join(', ')}</span>
                {' '}— saved with the item.
              </p>
            )
          )}
        </div>
      </div>
      {/* Names the offending fields rather than pointing at "the highlighted
          ones". The field that blocks an uploaded row is `unit`, which sits well
          below the fold on a long form — and the reporter came here to flip the
          VAT checkbox, so they had no reason to scroll to it or to touch it.
          A highlight nobody can see is not an explanation. (USC-BUG-0009/0010.) */}
      {!isItemFormValid && itemFormSubmitAttempted && (
        <div className="mt-3 text-xs text-red-600">
          <p>Please fix the following before submitting:</p>
          <ul className="list-disc ml-4 mt-1">
            {Object.entries(itemFormErrors).map(([field, message]) => (
              <li key={field}>{message}</li>
            ))}
            {rowIssues.some((i) => i?.level === 'error') && <li>Fix the unit conversion rows highlighted above.</li>}
          </ul>
        </div>
      )}
      {saveRefusal && (
        <div role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {saveRefusal}
        </div>
      )}
      {packPrompt && (
        <fieldset
          aria-label="Which price to keep"
          className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-slate-800"
        >
          <legend className="px-1 text-sm font-medium text-amber-900">
            Units / Case changes from {packPrompt.oldCount} to {packPrompt.newCount}. Which price do you want to keep?
          </legend>
          {(() => {
            const unit = packPrompt.unit.toLowerCase();
            // PCS is the vocabulary's word for "one of them"; a person says "piece".
            const piece = packPrompt.pieceUnit && packPrompt.pieceUnit.toUpperCase() !== 'PCS' ? packPrompt.pieceUnit.toLowerCase() : 'piece';
            // The server sends a piece price to 4 decimals; under a cent it is worded, not shown as 0.00.
            const money = (n: number) =>
              n > 0 && n < 0.01
                ? `under ${formatCurrency(0.01, itemForm.currency ?? null, { ceil: false })}`
                : formatCurrency(n, itemForm.currency ?? null, { ceil: false });
            const { keep_pack_price: keepPack, keep_piece_price: keepPiece } = packPrompt;
            const choices: Array<{ value: PackPriceChoice; text: string; disabledReason?: string }> = [
              {
                value: 'keep_pack_price',
                text: `Keep ${money(keepPack.packPrice)} per ${unit} — a ${piece} becomes ${money(keepPack.piecePrice)}`,
              },
              keepPiece.packPrice === null
                ? {
                    value: 'keep_piece_price',
                    text: `Keep ${money(keepPiece.piecePrice)} per ${piece}`,
                    disabledReason: `a ${unit} would cost under ${formatCurrency(0.01, itemForm.currency ?? null, { ceil: false })}; type the price instead`,
                  }
                : {
                    value: 'keep_piece_price',
                    text: `Keep ${money(keepPiece.piecePrice)} per ${piece} — a ${unit} becomes ${money(keepPiece.packPrice)}`,
                  },
            ];
            return choices.map((c) => (
              <label key={c.value} className={`mt-1 flex items-center gap-2${c.disabledReason ? ' text-slate-500' : ''}`}>
                <input
                  type="radio"
                  name="pack-price-choice"
                  checked={packChoice === c.value}
                  disabled={!!c.disabledReason}
                  onChange={() => setPackChoice(c.value)}
                />
                <span>{c.text}</span>
                {c.disabledReason && <span className="text-xs">({c.disabledReason})</span>}
              </label>
            ));
          })()}
          <p className="mt-1 text-xs text-slate-600">Nothing is saved until you choose.</p>
        </fieldset>
      )}
      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-slate-300 rounded-lg"
        >
          Cancel
        </button>
        {/* Disabled ONLY while a save is in flight — never for invalidity.
            `disabled={!isItemFormValid}` deadlocked the form: the banner that
            explains the refusal renders on `itemFormSubmitAttempted`, and the
            only thing that sets that flag is `handleSave`, which is this
            button's own onClick. A user editing a row uploaded without a unit
            got a greyed button, no message, and no way to produce one — and
            reported it twice, as "Save button isn't working" (USC-BUG-0010) and
            "VAT button isn't working" (USC-BUG-0009).

            `handleSave` still refuses an invalid form on its own first lines, so
            nothing invalid is submitted; the click now buys an explanation
            instead of nothing.

            The one other reason (D119.3): the server asked which price to keep
            and the question is on screen. That is not invalidity - the radio
            buttons above are the explanation and the way out. */}
        <button
          type="button"
          onClick={handleSave}
          disabled={savingItem || (packPrompt !== null && packChoice === null)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {savingItem ? 'Saving...' : submitLabel || (isEdit ? 'Save' : 'Add item')}
        </button>
      </div>
    </>
  );
}
